import { FC, useMemo } from "react";
import { Paper, Skeleton, Typography } from "@mui/material";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchFeatureConfiguration } from "src/Pages/Company/Absences/api/featureConfiguration.queries";
import { transformFeatureConfigToFormValues } from "src/utils/Company/Absences/parseFeatureConfiguration";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import AbsencesConfigurationForm from "./AbsencesConfigurationForm";

export interface IFormValues {
  [key: string]: boolean;
}

const AbsencesConfiguration: FC = () => {
  const basePath = useBasePath();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsencesConfiguration.tsx",
    });
  }

  const {
    data: featureConfigurationData,
    isFetching,
    isError,
  } = useFetchFeatureConfiguration(companyId);

  const defaultValues = useMemo(
    () => transformFeatureConfigToFormValues(featureConfigurationData),
    [featureConfigurationData]
  );

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Konfiguracja Nieobecności"),
          },
        ]}
      />
      <Title title={__("Konfiguracja Nieobecności")} />
      <Paper sx={{ p: "2rem" }} data-testid="absences-configuration">
        <Typography variant="h6" component="h2" mb="1rem">
          {__("Widoczność Modułu Nieobecności dla pracowników")}
        </Typography>
        <Typography variant="body1" color="text.secondary" margin="normal">
          {__(
            "Wyłączenie widoczności modułu spowoduje ukrycie go dla pracowników."
          )}
        </Typography>
        {/* eslint-disable-next-line no-nested-ternary */}
        {isError ? (
          <ErrorDataFetching />
        ) : isFetching ? (
          <Skeleton height="2rem" width="50%" sx={{ m: "1rem 0" }} />
        ) : (
          <AbsencesConfigurationForm defaultValues={defaultValues} />
        )}
      </Paper>
    </>
  );
};

export default AbsencesConfiguration;
